import React, { useState } from 'react';
import { Download, Upload, RefreshCw, FlaskConical, FolderOpen, FilePlus, HardDrive, X, Cloud, CloudOff, RotateCcw, ChevronDown, ChevronRight, BarChart2 } from 'lucide-react';
import { useBudget } from '../../context/BudgetContext';
import { ConfirmDialog, Modal, SearchableSelect } from '../Common/Common';
import { STORAGE_KEYS, DEFAULT_SETTINGS, DEFAULT_CATEGORIES } from '../../utils/constants';
import { isFileSystemSupported } from '../../utils/fileStorage';
import { getAllCurrencies, getCurrencyInfo, CURRENCY_LOCALE_MAP } from '../../utils/currencies';
import { formatCurrency } from '../../utils/formatting';

function Settings() {
  const {
    settings, updateSettings, exportJSON, importJSON, exportCSV, loadSampleData, showToast, transactions, categories, buckets,
    fileSyncEnabled, fileName, linkExistingFile, createNewFile, clearFileStorage,
    googleClientId,
    googleDriveOn, googleDriveEmail, googleDriveLastSync,
    googleDriveFileId, googleDriveFileName,
    isDriveConnecting, isDriveSyncing, driveNeedsReconnect, driveDirty,
    googleDriveAutoSave, setGoogleDriveAutoSave, saveToDriveNow,
    driveFiles, showFilePicker, closeDriveFilePicker,
    connectGoogleDrive, disconnectGoogleDrive, syncFromDrive, verifyDrive,
    selectDriveFile, switchDriveFile, renameDriveFileActive,
  } = useBudget();
  const [local, setLocal] = useState(settings);
  const [confirmReset, setConfirmReset] = useState(false);
  const [confirmSample, setConfirmSample] = useState(false);
  const [saved, setSaved] = useState(false);
  const builtInClientId = process.env.REACT_APP_GOOGLE_CLIENT_ID || '';
  const [draftClientId, setDraftClientId] = useState(googleClientId || builtInClientId);
  const [showDriveGuide, setShowDriveGuide] = useState(!builtInClientId);
  const [hostingTab, setHostingTab] = useState('local'); // 'local' | 'vm' | 'firebase'
  const [showRenameInput, setShowRenameInput] = useState(false);
  const [renameValue, setRenameValue] = useState('');
  const [newFileNameInput, setNewFileNameInput] = useState('');

  // Inline style reused for <code> snippets inside the Drive setup guide
  const mono = { fontFamily: 'var(--font-mono)', fontSize: 11 };

  /** Format a Drive last-sync ISO string into a short human-readable label. */
  function formatLastSync(iso) {
    if (!iso) return 'Never';
    const diff = Math.floor((Date.now() - new Date(iso)) / 1000);
    if (diff < 60)   return 'Just now';
    if (diff < 3600) return `${Math.floor(diff / 60)} min ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)} h ago`;
    return new Date(iso).toLocaleDateString();
  }

  function setF(k, v) { setLocal(p => ({ ...p, [k]: v })); setSaved(false); }

  function selectCurrency(code) {
    const info   = getCurrencyInfo(code);
    const locale = CURRENCY_LOCALE_MAP[code] || local.locale || navigator.language || 'en-US';
    setLocal(p => ({ ...p, currency: info.code, currencySymbol: info.symbol, locale }));
    setSaved(false);
  }

  function save() {
    updateSettings(local);
    setSaved(true);
    showToast('Settings saved');
  }

  function handleImport(e) {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => importJSON(ev.target.result);
    reader.readAsText(file);
    e.target.value = '';
  }

  const MONTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];

  function generateAnnualReport() {
    const year = new Date().getFullYear();
    const fmt = v => formatCurrency(v, settings);

    // Group by month × type
    const monthly = {};
    for (let m = 0; m < 12; m++) monthly[m] = { income: 0, expense: 0, savings: 0 };
    transactions.forEach(t => {
      if (!t.date.startsWith(String(year))) return;
      const m = parseInt(t.date.slice(5, 7), 10) - 1;
      if (m < 0 || m > 11) return;
      monthly[m][t.type] = (monthly[m][t.type] || 0) + t.amount;
    });

    // Top expense categories
    const catTotals = {};
    transactions.filter(t => t.type === 'expense' && t.date.startsWith(String(year))).forEach(t => {
      const cat = categories.find(c => c.id === t.category);
      const name = cat ? cat.name : t.category;
      catTotals[name] = (catTotals[name] || 0) + t.amount;
    });
    const topCats = Object.entries(catTotals).sort((a, b) => b[1] - a[1]).slice(0, 10);

    const totalIncome  = Object.values(monthly).reduce((s, m) => s + m.income, 0);
    const totalExpense = Object.values(monthly).reduce((s, m) => s + m.expense, 0);
    const totalSavings = Object.values(monthly).reduce((s, m) => s + m.savings, 0);
    const net = totalIncome - totalExpense - totalSavings;

    const html = `<!DOCTYPE html>
<html lang="en">
<head>
<meta charset="utf-8">
<title>Pennywise — ${year} Annual Summary</title>
<style>
  body { font-family: system-ui, sans-serif; max-width: 800px; margin: 40px auto; padding: 0 20px; color: #0f1117; }
  h1 { font-size: 22px; color: #6366f1; margin-bottom: 4px; }
  .subtitle { color: #666; font-size: 14px; margin-bottom: 32px; }
  .summary-grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 16px; margin-bottom: 32px; }
  .summary-box { border-radius: 10px; padding: 16px; }
  .income-box  { background: #ecfdf5; }
  .expense-box { background: #fef2f2; }
  .savings-box { background: #eff6ff; }
  .net-box     { background: ${net >= 0 ? '#ecfdf5' : '#fef2f2'}; }
  .summary-box .label { font-size: 11px; font-weight: 600; text-transform: uppercase; letter-spacing: .05em; color: #666; }
  .summary-box .amount { font-size: 20px; font-weight: 700; margin-top: 4px; font-family: 'Courier New', monospace; }
  table { width: 100%; border-collapse: collapse; margin-bottom: 32px; }
  th { background: #f0f2f5; text-align: left; padding: 8px 10px; font-size: 12px; }
  td { padding: 7px 10px; border-bottom: 1px solid #e4e7ef; font-size: 13px; font-family: 'Courier New', monospace; }
  tr:last-child td { border-bottom: none; }
  .mono { font-family: 'Courier New', monospace; }
  h2 { font-size: 15px; margin: 0 0 12px; }
  @media print { body { margin: 20px; } }
</style>
</head>
<body>
<h1>Pennywise — ${year} Annual Summary</h1>
<p class="subtitle">Generated ${new Date().toLocaleDateString()}</p>

<div class="summary-grid">
  <div class="summary-box income-box"><div class="label">Total Income</div><div class="amount">${fmt(totalIncome)}</div></div>
  <div class="summary-box expense-box"><div class="label">Total Expenses</div><div class="amount">${fmt(totalExpense)}</div></div>
  <div class="summary-box savings-box"><div class="label">Total Savings</div><div class="amount">${fmt(totalSavings)}</div></div>
  <div class="summary-box net-box"><div class="label">Net</div><div class="amount">${fmt(net)}</div></div>
</div>

<h2>Monthly Breakdown</h2>
<table>
  <thead><tr><th>Month</th><th>Income</th><th>Expenses</th><th>Savings</th><th>Net</th></tr></thead>
  <tbody>
    ${MONTHS.map((m, i) => {
      const d = monthly[i];
      const rowNet = d.income - d.expense - d.savings;
      return `<tr><td>${m}</td><td>${fmt(d.income)}</td><td>${fmt(d.expense)}</td><td>${fmt(d.savings)}</td><td style="color:${rowNet >= 0 ? '#059669' : '#dc2626'}">${fmt(rowNet)}</td></tr>`;
    }).join('')}
  </tbody>
</table>

<h2>Top Expense Categories</h2>
<table>
  <thead><tr><th>Category</th><th>Total</th><th>% of Expenses</th></tr></thead>
  <tbody>
    ${topCats.map(([name, amt]) => `<tr><td>${name}</td><td>${fmt(amt)}</td><td>${totalExpense > 0 ? ((amt / totalExpense) * 100).toFixed(1) : 0}%</td></tr>`).join('')}
  </tbody>
</table>

<p style="font-size:12px;color:#999;margin-top:32px">Generated by Pennywise &mdash; your local finance tracker</p>
</body></html>`;

    const blob = new Blob([html], { type: 'text/html' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `pennywise_${year}_annual_summary.html`;
    a.click();
    URL.revokeObjectURL(url);
    showToast(`${year} annual report downloaded`);
  }

  function hardReset() {
    Object.values(STORAGE_KEYS).forEach(k => { try { localStorage.removeItem(k); } catch {} });
    window.location.reload();
  }

  const storageEstimate = () => {
    try {
      const used = Object.values(STORAGE_KEYS).reduce((s, k) => {
        const v = localStorage.getItem(k) || '';
        return s + v.length;
      }, 0);
      return `~${(used / 1024).toFixed(1)} KB used`;
    } catch { return '—'; }
  };

  return (
    <div style={{ maxWidth: 560 }}>
      {/* Appearance */}
      <div className="card" style={{ marginBottom: 16 }}>
        <div className="card-header"><span className="card-title">Currency & locale</span></div>

        <div className="form-group">
          <label>Main (balance) currency</label>
          <SearchableSelect
            value={local.currency}
            onChange={selectCurrency}
            options={getAllCurrencies().map(c => ({
              value: c.code,
              label: `${c.name} (${c.code})`,
              secondary: c.symbol,
            }))}
            placeholder="Search currency…"
          />
          <p className="muted" style={{ fontSize: 12, marginTop: 5 }}>
            All amounts are stored and displayed in this currency. Foreign-currency transactions are converted to it using the exchange rate you provide.
          </p>
          <p className="muted" style={{ fontSize: 12, marginTop: 5 }}>
            Savings buckets can use different currencies — set per bucket in Savings. Historical FX rates are fetched from ECB (Frankfurter) and cached offline.
          </p>
        </div>

        <div style={{ display: 'flex', gap: 10 }} className="form-group">
          <div style={{ flex: 1 }}>
            <label>Symbol override</label>
            <input value={local.currencySymbol} onChange={e => setF('currencySymbol', e.target.value)} placeholder="₪" style={{ maxWidth: 80 }} />
          </div>
          <div style={{ flex: 2 }}>
            <label>Locale (number formatting)</label>
            <input value={local.locale} onChange={e => setF('locale', e.target.value)} placeholder="e.g. he-IL, en-US" />
          </div>
        </div>

        <div className="form-group">
          <label>First day of month</label>
          <select value={local.firstDayOfMonth} onChange={e => setF('firstDayOfMonth', parseInt(e.target.value))} style={{ maxWidth: 200 }}>
            <option value={1}>1st</option>
            <option value={10}>10th</option>
            <option value={15}>15th</option>
            <option value={20}>20th</option>
            <option value={25}>25th</option>
          </select>
        </div>

        <div className="form-group">
          <label>Budget input step</label>
          <p className="muted" style={{ fontSize: 12, marginBottom: 6 }}>
            Amount the ▲▼ arrows increment/decrement budget fields in the Budget Planner.
          </p>
          <div style={{ display: 'flex', gap: 6 }}>
            {[1, 10, 100, 500, 1000].map(s => (
              <button
                key={s}
                type="button"
                onClick={() => setF('budgetStep', s)}
                style={{
                  fontSize: 12, padding: '4px 12px', borderRadius: 6, cursor: 'pointer',
                  border: `1px solid ${(local.budgetStep || 100) === s ? 'var(--savings)' : 'var(--border)'}`,
                  background: (local.budgetStep || 100) === s ? 'var(--savings-bg)' : 'transparent',
                  color: (local.budgetStep || 100) === s ? 'var(--savings)' : 'var(--text-secondary)',
                  fontFamily: 'var(--font-mono)',
                }}
              >{s}</button>
            ))}
          </div>
        </div>

        <div className="form-group" style={{ marginBottom: 4 }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', userSelect: 'none' }}>
            <input
              type="checkbox"
              checked={local.colorBlindMode || false}
              onChange={e => setF('colorBlindMode', e.target.checked)}
            />
            Color blind mode
          </label>
          <p className="muted" style={{ fontSize: 12, marginTop: 4, marginLeft: 24 }}>
            Pie charts use 20 perceptually distinct colors instead of the red/blue palettes.
          </p>
        </div>

        <button className={`btn ${saved ? '' : 'btn-primary'}`} onClick={save} style={{ marginTop: 4 }}>
          {saved ? 'Saved' : 'Save settings'}
        </button>
      </div>

      {/* Storage location */}
      <div className="card" style={{ marginBottom: 16 }}>
        <div className="card-header"><span className="card-title">Storage location</span></div>
        {isFileSystemSupported() ? (
          <>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12, padding: '10px 14px', borderRadius: 8, background: 'var(--bg-secondary)', border: '1px solid var(--border)' }}>
              <HardDrive size={16} style={{ color: fileSyncEnabled ? 'var(--income)' : 'var(--text-tertiary)', flexShrink: 0 }} />
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 13, fontWeight: 500 }}>
                  {fileSyncEnabled ? 'File storage active' : 'Browser storage (localStorage)'}
                </div>
                <div className="muted" style={{ fontSize: 12 }}>
                  {fileSyncEnabled
                    ? `Auto-saving to: ${fileName}`
                    : `${storageEstimate()} · data stays in this browser only`}
                </div>
              </div>
              {fileSyncEnabled && (
                <button className="btn btn-ghost btn-icon btn-sm" onClick={clearFileStorage} title="Disconnect file">
                  <X size={14} />
                </button>
              )}
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              <button className="btn" onClick={linkExistingFile} style={{ flex: 1 }}>
                <FolderOpen size={14} /> Open existing file…
              </button>
              <button className="btn" onClick={createNewFile} style={{ flex: 1 }}>
                <FilePlus size={14} /> Create new file…
              </button>
            </div>
            <p className="muted" style={{ fontSize: 12, marginTop: 10 }}>
              <strong>Open existing</strong> loads data from a previously-saved budget JSON file (your data is replaced with the file's contents). <strong>Create new</strong> writes the current data to a new file.
              After linking, every change auto-saves to the file. It's a plain JSON you can back up, move, or open on another machine.
            </p>
          </>
        ) : (
          <p className="muted" style={{ fontSize: 13 }}>
            File storage requires a Chromium-based browser (Chrome, Edge). Currently using browser localStorage.
          </p>
        )}
      </div>

      {/* Google Drive sync */}
      <div className="card" style={{ marginBottom: 16 }}>
        <div className="card-header">
          <span className="card-title">Google Drive sync</span>
          {googleDriveOn && (
            <span style={{
              fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 99,
              background: 'var(--income-bg)', color: 'var(--income)',
            }}>Active</span>
          )}
          {driveNeedsReconnect && (
            <span style={{
              fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 99,
              background: 'var(--expense-bg)', color: 'var(--expense)',
            }}>Reconnect required</span>
          )}
        </div>

        <p className="muted" style={{ fontSize: 13, marginBottom: 14 }}>
          Sync data across all devices — including iPhone and iPad — via your
          Google Drive. Data is stored in a hidden app folder; it does not
          appear in your Drive file list.
        </p>

        {/* Connected state */}
        {googleDriveOn && !driveNeedsReconnect ? (
          <>
            <div style={{
              display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12,
              padding: '10px 14px', borderRadius: 8,
              background: 'var(--bg-secondary)', border: '1px solid var(--border)',
            }}>
              <Cloud size={16} style={{ color: 'var(--income)', flexShrink: 0 }} />
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 13, fontWeight: 500 }}>{googleDriveEmail}</div>
                <div className="muted" style={{ fontSize: 12 }}>
                  {isDriveSyncing ? 'Saving…'
                    : driveDirty ? 'Unsynced changes'
                    : `Last synced: ${formatLastSync(googleDriveLastSync)}`}
                </div>
                {/* Active file name + inline rename */}
                <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 3 }}>
                  File: <strong>{googleDriveFileName || 'budget-data.json'}</strong>
                  <button
                    className="btn btn-ghost btn-sm"
                    style={{ fontSize: 11, padding: '0 6px', marginLeft: 6, height: 20, lineHeight: '20px' }}
                    onClick={() => { setRenameValue(googleDriveFileName || 'budget-data'); setShowRenameInput(v => !v); }}
                  >
                    Rename
                  </button>
                </div>
                {showRenameInput && (
                  <div style={{ display: 'flex', gap: 6, marginTop: 6 }}>
                    <input
                      value={renameValue}
                      onChange={e => setRenameValue(e.target.value)}
                      style={{ flex: 1, fontSize: 12, padding: '3px 8px' }}
                      onKeyDown={e => {
                        if (e.key === 'Enter' && renameValue.trim()) { renameDriveFileActive(renameValue.trim()); setShowRenameInput(false); }
                        if (e.key === 'Escape') setShowRenameInput(false);
                      }}
                      autoFocus
                    />
                    <button
                      className="btn btn-primary btn-sm"
                      disabled={!renameValue.trim()}
                      onClick={() => { renameDriveFileActive(renameValue.trim()); setShowRenameInput(false); }}
                    >
                      Save
                    </button>
                  </div>
                )}
              </div>
              <button className="btn btn-ghost btn-icon btn-sm" onClick={disconnectGoogleDrive} title="Disconnect">
                <X size={14} />
              </button>
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
              <button
                className={`btn${driveDirty ? ' btn-primary' : ''}`}
                onClick={saveToDriveNow}
                disabled={isDriveSyncing || !driveDirty}
                style={{ flex: '1 1 calc(50% - 4px)', minWidth: 0 }}
                title="Push local changes to Drive now"
              >
                <Cloud size={14} /> {driveDirty ? 'Save now' : 'Saved'}
              </button>
              <button
                className="btn"
                onClick={syncFromDrive}
                disabled={isDriveSyncing}
                style={{ flex: '1 1 calc(50% - 4px)', minWidth: 0 }}
              >
                <RotateCcw size={14} /> Pull latest
              </button>
              <button
                className="btn"
                onClick={switchDriveFile}
                disabled={isDriveSyncing}
                style={{ flex: '1 1 calc(50% - 4px)', minWidth: 0 }}
                title="Switch to a different budget file"
              >
                Switch file
              </button>
              <button
                className="btn btn-ghost"
                onClick={verifyDrive}
                disabled={isDriveSyncing}
                style={{ flex: '1 1 calc(50% - 4px)', minWidth: 0 }}
                title="Check what's stored in Drive without overwriting local data"
              >
                Verify
              </button>
            </div>

            <label style={{
              display: 'flex', alignItems: 'center', gap: 8,
              marginTop: 14, fontSize: 13, cursor: 'pointer',
            }}>
              <input
                type="checkbox"
                checked={googleDriveAutoSave}
                onChange={e => setGoogleDriveAutoSave(e.target.checked)}
                style={{ flexShrink: 0 }}
              />
              Auto-save changes to Drive
            </label>
            <div className="muted" style={{ fontSize: 12, marginLeft: 24, marginTop: 2 }}>
              {googleDriveAutoSave
                ? 'Saves a few seconds after each change.'
                : 'Off — use “Save now” to push changes manually.'}
            </div>
          </>
        ) : (
          <>
            {/* Reconnect or first-time connect */}
            {(driveNeedsReconnect && googleDriveEmail) && (
              <div style={{
                display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12,
                padding: '10px 14px', borderRadius: 8,
                background: 'var(--warning-bg, #fef3c7)', border: '1px solid var(--color-warning)',
              }}>
                <CloudOff size={16} style={{ color: 'var(--color-warning)', flexShrink: 0 }} />
                <div style={{ flex: 1, fontSize: 13 }}>
                  Session expired for <strong>{googleDriveEmail}</strong>
                </div>
              </div>
            )}

            {/* ── Client ID input (hidden when already baked in via env var) ── */}
            {!builtInClientId && (
              <div className="form-group" style={{ marginBottom: 12 }}>
                <label>Google OAuth Client ID</label>
                <input
                  value={draftClientId}
                  onChange={e => setDraftClientId(e.target.value.trim())}
                  placeholder="Paste your Client ID here…"
                  style={{ fontFamily: 'var(--font-mono)', fontSize: 12 }}
                />
              </div>
            )}

            <button
              className="btn btn-primary"
              style={{ width: '100%', marginBottom: 12 }}
              disabled={!draftClientId || isDriveConnecting}
              onClick={() => connectGoogleDrive(draftClientId)}
            >
              <Cloud size={14} />
              {isDriveConnecting ? 'Connecting…' : driveNeedsReconnect ? 'Reconnect to Google Drive' : 'Connect Google Drive'}
            </button>

            {/* ── Setup guide ─────────────────────────────────────────────── */}
            <button
              className="btn btn-ghost"
              style={{ width: '100%', justifyContent: 'flex-start', fontSize: 13 }}
              onClick={() => setShowDriveGuide(v => !v)}
            >
              {showDriveGuide ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
              {builtInClientId
                ? 'How to authorise a new device / URL'
                : 'How to get a Client ID — step-by-step (~5 min)'}
            </button>

            {showDriveGuide && (
              <div style={{
                marginTop: 8, padding: '14px 16px', borderRadius: 8,
                background: 'var(--bg-secondary)', border: '1px solid var(--border)',
                fontSize: 13, lineHeight: 1.8,
              }}>

                {/* Step 1 — only shown when no built-in key */}
                {!builtInClientId && (
                  <>
                    <p style={{ margin: '0 0 8px', fontWeight: 600 }}>Step 1 — Create OAuth credentials</p>
                    <ol style={{ margin: '0 0 16px', paddingLeft: 20 }}>
                      <li>
                        Open{' '}
                        <a href="https://console.cloud.google.com/" target="_blank" rel="noreferrer"
                          style={{ color: 'var(--savings)' }}>console.cloud.google.com</a>
                        {' '}and create a new project (or select an existing one).
                      </li>
                      <li>
                        In the left menu go to{' '}
                        <strong>APIs &amp; Services → Library</strong>.
                        Search for <strong>Google Drive API</strong> and click <strong>Enable</strong>.
                      </li>
                      <li>
                        Go to <strong>APIs &amp; Services → Credentials</strong> →
                        click <strong>+ Create Credentials → OAuth client ID</strong>.
                      </li>
                      <li>
                        Choose <strong>Web application</strong> as the application type.
                      </li>
                      <li>
                        Under <strong>Authorised JavaScript origins</strong> add the
                        URL(s) from Step 2 below, then click <strong>Create</strong>.
                      </li>
                      <li>
                        Copy the <strong>Client ID</strong> and paste it into the field above.
                      </li>
                    </ol>
                    <hr style={{ border: 'none', borderTop: '1px solid var(--border)', margin: '0 0 14px' }} />
                  </>
                )}

                {/* Step 2 — always shown */}
                <p style={{ margin: '0 0 10px', fontWeight: 600 }}>
                  {builtInClientId ? 'Add your app\'s URL as an Authorised JavaScript origin' : 'Step 2 — Which URL to add'}
                </p>
                <p className="muted" style={{ fontSize: 12, margin: '0 0 10px' }}>
                  Google only allows the OAuth popup on URLs you explicitly whitelist.
                  Pick your setup:
                </p>

                {/* Hosting tab selector */}
                <div style={{ display: 'flex', gap: 6, marginBottom: 12 }}>
                  {[
                    { id: 'local',    label: '🖥️ Local / PC' },
                    { id: 'vm',       label: '☁️ VM / Server' },
                    { id: 'firebase', label: '🔥 Firebase' },
                  ].map(tab => (
                    <button
                      key={tab.id}
                      type="button"
                      onClick={() => setHostingTab(tab.id)}
                      style={{
                        fontSize: 12, padding: '4px 10px', borderRadius: 6, cursor: 'pointer',
                        border: `1px solid ${hostingTab === tab.id ? 'var(--savings)' : 'var(--border)'}`,
                        background: hostingTab === tab.id ? 'var(--savings-bg)' : 'transparent',
                        color: hostingTab === tab.id ? 'var(--savings)' : 'var(--text-secondary)',
                        fontFamily: 'inherit',
                      }}
                    >{tab.label}</button>
                  ))}
                </div>

                {hostingTab === 'local' && (
                  <div>
                    <p style={{ margin: '0 0 6px' }}>
                      You're running the app with <code style={mono}>npm start</code> or{' '}
                      <code style={mono}>npx serve -s build</code> on your own PC.
                    </p>
                    <p style={{ margin: '0 0 6px' }}>Add this origin:</p>
                    <code style={{ ...mono, display: 'block', padding: '6px 10px', borderRadius: 6, background: 'var(--bg-primary)', border: '1px solid var(--border)', marginBottom: 8 }}>
                      http://localhost:3000
                    </code>
                    <p className="muted" style={{ fontSize: 12, margin: 0 }}>
                      To access from your phone on the same Wi-Fi, also add your PC's local IP, e.g.{' '}
                      <code style={mono}>http://192.168.1.x:3000</code>. Find it with{' '}
                      <code style={mono}>ipconfig</code> (Windows) or <code style={mono}>ifconfig</code> (Mac/Linux).
                    </p>
                  </div>
                )}

                {hostingTab === 'vm' && (
                  <div>
                    <p style={{ margin: '0 0 6px' }}>
                      You're serving the app from a VPS, home server, or virtual machine.
                    </p>
                    <p style={{ margin: '0 0 4px' }}>Add <em>one</em> of these depending on your setup:</p>
                    <table style={{ fontSize: 12, width: '100%', borderCollapse: 'collapse', marginBottom: 8 }}>
                      <tbody>
                        {[
                          ['Custom domain + HTTPS', 'https://your-domain.com'],
                          ['Custom domain, no HTTPS', 'http://your-domain.com'],
                          ['IP address + port', 'http://123.45.67.89:3000'],
                          ['Local network IP', 'http://192.168.1.x:3000'],
                        ].map(([label, url]) => (
                          <tr key={label}>
                            <td style={{ padding: '3px 10px 3px 0', color: 'var(--text-secondary)', whiteSpace: 'nowrap' }}>{label}</td>
                            <td style={{ padding: '3px 0' }}><code style={mono}>{url}</code></td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    <p className="muted" style={{ fontSize: 12, margin: 0 }}>
                      Add every URL you'll open the app from. You can add more origins later without regenerating the Client ID.
                    </p>
                  </div>
                )}

                {hostingTab === 'firebase' && (
                  <div>
                    <p style={{ margin: '0 0 6px' }}>
                      You're deploying with <code style={mono}>firebase deploy</code>.
                      Firebase gives every project <strong>two</strong> default URLs — add both:
                    </p>
                    <code style={{ ...mono, display: 'block', padding: '6px 10px', borderRadius: 6, background: 'var(--bg-primary)', border: '1px solid var(--border)', marginBottom: 4 }}>
                      https://YOUR_PROJECT_ID.web.app
                    </code>
                    <code style={{ ...mono, display: 'block', padding: '6px 10px', borderRadius: 6, background: 'var(--bg-primary)', border: '1px solid var(--border)', marginBottom: 8 }}>
                      https://YOUR_PROJECT_ID.firebaseapp.com
                    </code>
                    <p className="muted" style={{ fontSize: 12, margin: '0 0 6px' }}>
                      Replace <code style={mono}>YOUR_PROJECT_ID</code> with the project ID shown in
                      the Firebase Console → Project settings (e.g. <code style={mono}>budget-app-a1b2c</code>).
                    </p>
                    <p className="muted" style={{ fontSize: 12, margin: 0 }}>
                      If you set up a custom domain in Firebase Hosting, add that URL too.
                    </p>
                  </div>
                )}

                {/* "App not verified" note */}
                <div style={{
                  marginTop: 14, padding: '8px 12px', borderRadius: 6,
                  background: 'var(--warning-bg, #fef3c7)', border: '1px solid var(--color-warning)',
                  fontSize: 12,
                }}>
                  <strong>Note:</strong> Google may show an "app not verified" warning on the first sign-in.
                  Click <strong>Advanced → Go to app (unsafe)</strong> — this is normal for personal projects
                  that haven't completed Google's app verification process.
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Design system */}
      <div className="card" style={{ marginBottom: 16 }}>
        <div className="card-header"><span className="card-title">Design system</span></div>
        <p className="secondary" style={{ fontSize: 13, marginBottom: 12 }}>
          Open the design book to preview components and customize colors, radii, shadows, and typography.
          Saving writes <code style={{ fontFamily: 'var(--font-mono)', fontSize: 11 }}>src/styles/tokens.css</code>; reload the app to see changes.
        </p>
        <a href="/design-book.html" target="_blank" rel="noreferrer" className="btn">
          Open design book ↗
        </a>
      </div>

      {/* Data management */}
      <div className="card" style={{ marginBottom: 16 }}>
        <div className="card-header"><span className="card-title">Data management</span></div>
        <p className="secondary" style={{ fontSize: 13, marginBottom: 16 }}>
          {fileSyncEnabled
            ? `Data is auto-saved to ${fileName}. Use JSON export as an additional backup.`
            : `All data is stored locally in your browser. ${storageEstimate()}. Export regularly to back up your data.`}
        </p>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          <div style={{ display: 'flex', gap: 8 }}>
            <button className="btn" onClick={exportJSON} style={{ flex: 1 }}>
              <Download size={14} /> Export JSON backup
            </button>
            <button className="btn" onClick={exportCSV} style={{ flex: 1 }}>
              <Download size={14} /> Export transactions CSV
            </button>
          </div>
          <button className="btn" onClick={generateAnnualReport}>
            <BarChart2 size={14} /> Generate {new Date().getFullYear()} annual report
          </button>
          <label className="btn" style={{ cursor: 'pointer', justifyContent: 'center' }}>
            <Upload size={14} /> Import JSON backup
            <input type="file" accept=".json" onChange={handleImport} style={{ display: 'none' }} />
          </label>
          <button className="btn" onClick={() => setConfirmSample(true)}>
            <FlaskConical size={14} /> Load sample data
          </button>
        </div>
      </div>

      {/* Danger zone */}
      <div className="card" style={{ borderColor: 'var(--expense-border)' }}>
        <div className="card-header"><span className="card-title" style={{ color: 'var(--expense)' }}>Danger zone</span></div>
        <p className="secondary" style={{ fontSize: 13, marginBottom: 16 }}>
          This will delete ALL data including transactions, budgets, and savings. This action cannot be undone.
        </p>
        <button className="btn btn-danger" onClick={() => setConfirmReset(true)}>
          <RefreshCw size={14} /> Reset all data
        </button>
      </div>

      {confirmReset && (
        <ConfirmDialog
          title="Reset all data"
          message="This will permanently delete all your transactions, budgets, savings buckets, and settings. This cannot be undone. Export a backup first if needed."
          danger
          onConfirm={hardReset}
          onCancel={() => setConfirmReset(false)}
        />
      )}

      {confirmSample && (
        <ConfirmDialog
          title="Load sample data"
          message="This will replace your current transactions, budget, and savings with demo data. Your settings will not change. Export a backup first if you want to keep your existing data."
          onConfirm={() => { loadSampleData(); setConfirmSample(false); }}
          onCancel={() => setConfirmSample(false)}
        />
      )}

      {/* Drive file picker */}
      {showFilePicker && (
        <Modal
          title="Choose a budget file"
          onClose={() => closeDriveFilePicker()}
          footer={
            <button className="btn" onClick={() => closeDriveFilePicker()}>Cancel</button>
          }
        >
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8, minWidth: 300 }}>
            {driveFiles.length > 0 ? driveFiles.map(f => (
              <div
                key={f.id}
                style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                  padding: '8px 12px', borderRadius: 8,
                  background: googleDriveFileId === f.id ? 'var(--savings-bg)' : 'var(--bg-secondary)',
                  border: `1px solid ${googleDriveFileId === f.id ? 'var(--savings)' : 'var(--border)'}`,
                }}
              >
                <div>
                  <div style={{ fontSize: 13, fontWeight: 500 }}>{f.name.replace(/\.json$/, '')}</div>
                  <div className="muted" style={{ fontSize: 11 }}>
                    {new Date(f.modifiedTime).toLocaleDateString()}
                    {googleDriveFileId === f.id && ' · current'}
                  </div>
                </div>
                <button
                  className="btn btn-primary btn-sm"
                  onClick={() => selectDriveFile(f)}
                  disabled={googleDriveFileId === f.id}
                >
                  {googleDriveFileId === f.id ? 'Active' : 'Select'}
                </button>
              </div>
            )) : (
              <p className="muted" style={{ fontSize: 13, margin: 0 }}>No budget files found in Drive.</p>
            )}
            <div className="muted" style={{ fontSize: 12, textAlign: 'center', margin: '4px 0' }}>
              — or create new —
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              <input
                value={newFileNameInput}
                onChange={e => setNewFileNameInput(e.target.value)}
                placeholder="New file name…"
                style={{ flex: 1 }}
                onKeyDown={e => {
                  if (e.key === 'Enter' && newFileNameInput.trim()) {
                    selectDriveFile(null, newFileNameInput.trim());
                    setNewFileNameInput('');
                  }
                }}
              />
              <button
                className="btn btn-primary"
                disabled={!newFileNameInput.trim()}
                onClick={() => { selectDriveFile(null, newFileNameInput.trim()); setNewFileNameInput(''); }}
              >
                Create
              </button>
            </div>
          </div>
        </Modal>
      )}

      {/* Keyboard shortcuts reference */}
      <div className="card" style={{ marginTop: 16 }}>
        <div className="card-header"><span className="card-title">Keyboard shortcuts</span></div>
        <table style={{ fontSize: 13 }}>
          <tbody>
            {[
              ['T', 'Set date to today (in Tracker date field)'],
              ['Y', 'Set date to yesterday'],
              ['1', 'Select Income type'],
              ['2', 'Select Expense type'],
              ['3', 'Select Savings type'],
              ['Alt+G', 'Quick-select Groceries category'],
              ['Alt+T', 'Quick-select Transport category'],
              ['Alt+U', 'Quick-select Utilities category'],
              ['Alt+F', 'Quick-select Dining Out category'],
              ['Alt+E', 'Quick-select Entertainment category'],
              ['Enter', 'Submit transaction form'],
              ['Esc', 'Clear / reset form'],
            ].map(([k, d]) => (
              <tr key={k}>
                <td style={{ paddingRight: 16, paddingBottom: 8, whiteSpace: 'nowrap' }}><span className="kbd">{k}</span></td>
                <td style={{ paddingBottom: 8, color: 'var(--text-secondary)' }}>{d}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default React.memo(Settings);
